import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  TextField,
  MenuItem,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DateTimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { utcToLocal, localToUtc } from "../../Utils/timeConversions";
import { fetchFixSessions } from "../../http/api";
import { useAuth } from "../../Context/AuthProvider";
import { headerStyle, modalStyle, inputStyle } from "../OrderEntry/styles";
import { tokens, withAlpha } from "../../Common/designTokens";

const QuickFixFilterModal = ({ open, onClose, onApplyFilters, initialFilters = {} }) => {
  const { token } = useAuth();

  const ALL_SENTINEL = "__ALL__";

  // Filter state - now using single session field
  const [filters, setFilters] = useState({
    timeFrom: dayjs().startOf('day'),
    timeTo: dayjs().endOf('day'),
    session: ALL_SENTINEL, // single session field
    textIncludes: [],
    textExcludes: [],
  });

  // Options state - now single sessions list
  const [sessions, setSessions] = useState([]);

  // Input state for text includes/excludes
  const [includeInput, setIncludeInput] = useState("");
  const [excludeInput, setExcludeInput] = useState("");

  // Loading state
  const [loading, setLoading] = useState(false);

  const sanitizeTextList = (list = []) =>
    (list || []).map((item) => item?.toString().trim()).filter(Boolean);

  // Fetch sessions on mount
  useEffect(() => {
    if (open && token) {
      const fetchOptions = async () => {
        try {
          const sessionsData = await fetchFixSessions(token);
          setSessions(sessionsData || []);
        } catch (error) {
          console.error("Error fetching sessions:", error);
          setSessions([]);
        }
      };
      fetchOptions();
    }
  }, [open, token]);

  // Initialize filters when modal opens
  useEffect(() => {
    if (open) {
      // Reconstruct session from initialFilters sender+target
      const initialSession =
        initialFilters.sendercompid && initialFilters.targetcompid
          ? `${initialFilters.sendercompid}-${initialFilters.targetcompid}`
          : ALL_SENTINEL;

      setFilters({
        timeFrom: initialFilters.timeFrom
          ? (utcToLocal(initialFilters.timeFrom) ?? dayjs().startOf('day'))
          : dayjs().startOf('day'),
        timeTo: initialFilters.timeTo
          ? (utcToLocal(initialFilters.timeTo) ?? dayjs().endOf('day'))
          : dayjs().endOf('day'),
        session: initialSession,
        textIncludes: initialFilters.textIncludes || [],
        textExcludes: initialFilters.textExcludes || [],
      });
      setIncludeInput("");
      setExcludeInput("");
    }
  }, [open, initialFilters]);

  // helper to normalize incoming initial values (undefined/null/"" => ALL_SENTINEL)
  const normalizeCompId = (v) => (v == null || v === "" ? ALL_SENTINEL : v);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const addTextFilter = (type, value) => {
    if (!value.trim()) return;

    const field = type === 'include' ? 'textIncludes' : 'textExcludes';
    setFilters(prev => ({
      ...prev,
      [field]: [...prev[field], value.trim()]
    }));

    if (type === 'include') {
      setIncludeInput("");
    } else {
      setExcludeInput("");
    }
  };

  const removeTextFilter = (type, index) => {
    const field = type === 'include' ? 'textIncludes' : 'textExcludes';
    setFilters(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleKeyPress = (event, type) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = type === 'include' ? includeInput : excludeInput;
      addTextFilter(type, value);
    }
  };

  const handleApplyFilters = useCallback(async () => {
    setLoading(true);
    try {
      const sanitizedIncludes = sanitizeTextList(filters.textIncludes);
      const sanitizedExcludes = sanitizeTextList(filters.textExcludes);

      // Split session back into sender and target for API
      let sendercompid = null;
      let targetcompid = null;

      if (filters.session && filters.session !== ALL_SENTINEL) {
        const parts = filters.session.split('-');
        if (parts.length === 2) {
          sendercompid = parts[0].trim() || null;
          targetcompid = parts[1].trim() || null;
        }
      }

      const apiFilters = {
        timeFrom: localToUtc(filters.timeFrom) || undefined,
        timeTo: localToUtc(filters.timeTo) || undefined,
        sendercompid,
        targetcompid,
        textIncludes: sanitizedIncludes.length ? sanitizedIncludes : undefined,
        textExcludes: sanitizedExcludes.length ? sanitizedExcludes : undefined,
      };

      const cleanedFilters = Object.fromEntries(
        Object.entries(apiFilters).filter(([_, value]) => value != null)
      );

      await onApplyFilters(cleanedFilters);
      onClose();
    } catch (error) {
      console.error("Error applying filters:", error);
    } finally {
      setLoading(false);
    }
  }, [filters, onApplyFilters, onClose, sanitizeTextList]);

  const handleClearFilters = () => {
    setFilters({
      timeFrom: dayjs().startOf('day'),
      timeTo: dayjs().endOf('day'),
      session: ALL_SENTINEL,
      textIncludes: [],
      textExcludes: [],
    });
    setIncludeInput("");
    setExcludeInput("");
  };

  const datePickerStyles = {
    ...inputStyle,
    "& .MuiFilledInput-root": {
      ...inputStyle["& .MuiFilledInput-root"],
      paddingTop: "10px",
      borderRadius: "8px",
      backgroundColor: tokens.colors.white,
    },
    "& .MuiInputLabel-root": {
      color: tokens.colors.fontColour85,
      "&.Mui-focused": {
        color: tokens.palette.primary,
      },
    },
    "& .MuiFilledInput-input": {
      color: tokens.colors.fontColour,
      padding: "14px !important",
    },
  };

  // Larger chip style to match Inventory FilterChips (non-compact)
  const baseChipStyle = {
    borderRadius: "8px",
    fontFamily: "Lato",
    fontWeight: 700,
    fontSize: "14px",
    lineHeight: "16px",
    textTransform: "none",
    backgroundColor: tokens.colors.defaultGridRowColor,
    color: tokens.colors.fontColour,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    "& .MuiChip-deleteIcon": {
      color: tokens.colors.fontColour,
      fontSize: "14px",
    },
    "& .MuiChip-label": {
      padding: "0 8px",
    },
  };

  const chipStyle = {
    ...baseChipStyle,
    // primary tinted variant for includes
    backgroundColor: withAlpha(tokens.palette.primary, 0.08),
    color: tokens.palette.primary,
    border: `1px solid ${withAlpha(tokens.palette.primary, 0.25)}`,
    "& .MuiChip-deleteIcon": { color: tokens.palette.primary },
  };
  const excludeChipStyle = {
    ...baseChipStyle,
    backgroundColor: withAlpha(tokens.palette.danger, 0.08),
    color: tokens.palette.danger,
    border: `1px solid ${withAlpha(tokens.palette.danger, 0.25)}`,
    "& .MuiChip-deleteIcon": { color: tokens.palette.danger },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal
        open={open}
        onClose={onClose}
        disableEscapeKeyDown={true}
        aria-labelledby="quickfix-filter-modal-title"
      >
        <Box sx={{
          ...modalStyle,
          width: '600px',
          maxWidth: '90vw',
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
        }}>
          {/* Header */}
          <Box sx={{ ...headerStyle, flexShrink: 0 }}>
            <Typography
              id="quickfix-filter-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: "bold" }}
            >
              Filter QuickFIX Logs
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{
            flex: 1,
            overflow: "auto",
            p: 2,
          }}>
            <Grid container spacing={2}>
              {/* Date Range */}
              <Grid item xs={6}>
                <DateTimePicker
                  label="From Date/Time"
                  value={filters.timeFrom}
                  onChange={(value) => handleFilterChange('timeFrom', value)}
                  maxDateTime={filters.timeTo}
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      sx: datePickerStyles,
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <DateTimePicker
                  label="To Date/Time"
                  value={filters.timeTo}
                  onChange={(value) => handleFilterChange('timeTo', value)}
                  minDateTime={filters.timeFrom}
                  slotProps={{
                    textField: {
                      variant: "filled",
                      fullWidth: true,
                      sx: datePickerStyles,
                      InputProps: { disableUnderline: true },
                    },
                  }}
                />
              </Grid>

              {/* Session Dropdown */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  variant="filled"
                  label="Session"
                  value={filters.session}
                  onChange={(e) => handleFilterChange('session', e.target.value)}
                  sx={inputStyle}
                  InputProps={{ disableUnderline: true }}
                >
                  <MenuItem value={ALL_SENTINEL}>All Sessions</MenuItem>
                  {sessions.map((session) => {
                    const sessionKey = `${session.senderCompId}-${session.targetCompId}`;
                    return (
                      <MenuItem key={sessionKey} value={sessionKey}>
                        {sessionKey}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              {/* Text Includes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Text Includes (press Enter to add)"
                  value={includeInput}
                  onChange={(e) => setIncludeInput(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, 'include')}
                  sx={inputStyle}
                  InputProps={{ disableUnderline: true }}
                />
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filters.textIncludes.map((text, index) => (
                    <Chip
                      key={index}
                      label={text}
                      onDelete={() => removeTextFilter('include', index)}
                      sx={chipStyle}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Text Excludes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="filled"
                  label="Text Excludes (press Enter to add)"
                  value={excludeInput}
                  onChange={(e) => setExcludeInput(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, 'exclude')}
                  sx={inputStyle}
                  InputProps={{ disableUnderline: true }}
                />
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {filters.textExcludes.map((text, index) => (
                    <Chip
                      key={index}
                      label={text}
                      onDelete={() => removeTextFilter('exclude', index)}
                      sx={excludeChipStyle}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Footer */}
          <Box sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            p: 2,
            borderTop: `1px solid ${tokens.colors.bodyBorderColor50}`,
            backgroundColor: tokens.colors.defaultGridRowColor,
          }}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              sx={{
                textTransform: "none",
                fontWeight: "700",
                px: 3,
                py: 1,
                borderRadius: "8px",
              }}
            >
              Clear All
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  textTransform: "none",
                  fontWeight: "700",
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: "700",
                  px: 3,
                  py: 1,
                  borderRadius: "8px",
                  backgroundColor: tokens.palette.primary,
                  color: tokens.colors.white,
                  "&:hover": { backgroundColor: tokens.palette.primaryA80 },
                }}
              >
                {loading ? "Applying..." : "Apply Filters"}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </LocalizationProvider>
  );
};

export default QuickFixFilterModal;
